// InnerEaze product page (sections/ie-product.liquid).
// Option pills -> variant (price, availability, ?variant= URL, image), quantity, Add to Cart through
// /cart/add.js with Atelier's cart drawer refresh (same CartLinesUpdateEvent as assets/ie-quick-add.js),
// "or Buy It Now" cart link, Find My Size, gallery (thumbnails, swipe, hover zoom), sticky bars,
// Copy Link and the "Why You'll Love It" reveal.
// Atelier scrolls .page-wrapper (not the window) from 990px up, so scroll work uses a capture listener
// on document and getBoundingClientRect().
import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const still = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const DESK = '(min-width: 860px)';
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// Same output as Liquid's money_without_trailing_zeros for the shop's money format
function formatMoney(cents, format) {
  return String(format || '{{amount}}').replace(/{{\s*(\w+)\s*}}/g, (_, k) => {
    let th = ',';
    let dec = '.';
    let prec = 2;
    if (k === 'amount_no_decimals') prec = 0;
    else if (k === 'amount_with_comma_separator') { th = '.'; dec = ','; }
    else if (k === 'amount_no_decimals_with_comma_separator') { th = '.'; prec = 0; }
    else if (k === 'amount_no_decimals_with_space_separator') { th = ' '; prec = 0; }
    else if (k === 'amount_with_space_separator') { th = ' '; dec = ','; }
    else if (k === 'amount_with_period_and_space_separator') { th = ' '; }
    else if (k === 'amount_with_apostrophe_separator') { th = "'"; }
    let [a, b = ''] = (cents / 100).toFixed(prec).split('.');
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (d) => d + th);
    return prec > 0 && !/^0+$/.test(b) ? a + dec + b : a;
  });
}

class IeProduct extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-pd-data]');
    if (!dataEl) return;
    try { this.d = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const q = (s) => this.querySelector(s);
    this.q = q;
    this.form = q('form.ie-pd__form');
    this.groups = [...this.querySelectorAll('fieldset[data-opt]')];
    this.atc = q('[data-atc]');
    this.atcText = q('[data-atc-text]');
    this.stBtns = [...this.querySelectorAll('[data-st-atc]')];
    this.buyNow = q('[data-buynow]');
    this.qtyIn = q('[data-qty-in]');
    this.idIn = q('[data-variant-id]');
    this.msgEl = q('[data-msg]');
    this.top = q('[data-top]');
    this.bot = q('[data-bot]');
    this.why = q('[data-why]');

    // current choice per option: the checked pill, or null (Size starts empty unless the URL names a variant)
    const first = this.d.variants[0];
    this.sel = first.options.map((o, i) => {
      const g = this.groups.find((x) => Number(x.dataset.opt) === i);
      if (!g) return o;
      const r = g.querySelector('input[type="radio"]:checked');
      return r ? r.value : null;
    });

    this.ac = new AbortController();
    const sig = { signal: this.ac.signal };
    this.bindForm(sig);
    this.bindGallery(sig);
    this.bindFms(sig);
    this.bindShare(sig);

    if (this.why && !still()) this.why.classList.add('is-anim');
    let raf = 0;
    this.onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; this.tick(); });
    };
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true, signal: this.ac.signal });
    window.addEventListener('resize', () => { this.onScroll(); this.fitTrack(); }, sig);
    this.update(false);
    this.tick();
    // late layout (fonts, images) can move things: check again shortly after load
    setTimeout(() => this.tick(), 400);
  }

  disconnectedCallback() {
    if (this.ac) this.ac.abort();
    document.body.classList.remove('ie-has-sticky-bar');
    clearTimeout(this.shareT);
  }

  // ---------- variants ----------
  money(c) { return formatMoney(c, this.d.moneyFormat); }

  matches(v, skip) {
    return v.options.every((o, j) => j === skip || this.sel[j] == null || o === this.sel[j]);
  }

  bindForm(sig) {
    this.form.addEventListener('change', (e) => {
      const r = e.target;
      if (r.type !== 'radio') return;
      const g = r.closest('fieldset[data-opt]');
      if (!g) return;
      this.sel[Number(g.dataset.opt)] = r.value;
      this.update(true);
    }, sig);
    // Add to Cart and "or Buy It Now" are both submit buttons of the product form
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.add(!!(e.submitter && e.submitter.hasAttribute('data-buynow')));
    }, sig);
    this.form.addEventListener('click', (e) => {
      const b = e.target.closest('[data-qty]');
      if (!b) return;
      this.setQty(this.qty() + Number(b.dataset.qty));
    }, sig);
    this.qtyIn.addEventListener('change', () => this.setQty(this.qty()), sig);
    this.stBtns.forEach((b) => b.addEventListener('click', () => {
      if (!this.variant) this.need(true);
      else this.add();
    }, sig));
  }

  qty() {
    const n = parseInt(this.qtyIn.value, 10);
    return Number.isFinite(n) ? n : 1;
  }

  setQty(n) {
    this.qtyIn.value = Math.max(1, Math.min(99, n || 1));
  }

  update(user) {
    const d = this.d;
    const complete = this.sel.every((x) => x != null);
    const v = complete ? d.variants.find((x) => this.matches(x, -1)) || null : null;
    this.variant = v;
    const shown = v || d.variants.find((x) => this.matches(x, -1)) || d.variants[0];
    this.shown = shown;

    this.groups.forEach((g) => {
      const i = Number(g.dataset.opt);
      const nm = g.querySelector('[data-selname]');
      if (nm) nm.textContent = this.sel[i] || '';
      g.querySelectorAll('input[type="radio"]').forEach((r) => {
        const ok = d.variants.some((x) => x.available && x.options[i] === r.value && this.matches(x, i));
        r.closest('label').classList.toggle('is-out', !ok);
      });
    });

    const price = this.q('[data-price]');
    if (price) price.textContent = this.money(shown.price);
    const cmp = this.q('[data-compare]');
    if (cmp) {
      const on = shown.compare > shown.price;
      cmp.hidden = !on;
      if (on) cmp.textContent = this.money(shown.compare);
    }
    const pc = this.q('[data-pc]');
    if (pc && d.pack > 1) pc.textContent = this.money(Math.round(shown.price / d.pack / 100) * 100) + ' per piece';
    this.querySelectorAll('[data-st-price]').forEach((el) => { el.textContent = this.money(shown.price); });

    if (v) this.idIn.value = v.id;
    const anyOk = d.variants.some((x) => x.available && this.matches(x, -1));
    const sold = v ? !v.available : !anyOk;
    this.atc.disabled = sold;
    this.atcText.textContent = sold ? 'Sold Out' : 'Add to Cart';
    this.stBtns.forEach((b) => { b.disabled = sold; b.textContent = sold ? 'Sold Out' : 'Add to Cart'; });
    this.buyNow.hidden = sold;

    const stVar = this.q('[data-st-var]');
    if (stVar) {
      stVar.textContent = this.groups.map((g) => {
        const val = this.sel[Number(g.dataset.opt)];
        if (g.classList.contains('is-size')) return val ? 'Size ' + val : 'Select a size';
        return val || '';
      }).filter(Boolean).join(' · ');
    }

    if (user && v) {
      this.msg('');
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', u.toString());
      } catch (e) { /* ignore */ }
      if (v.media && this.slides) {
        const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(v.media));
        if (i >= 0) {
          this.go(i);
          const img = this.slides[i].querySelector('img');
          const st = this.q('.ie-pd__st-th img');
          if (img && st) st.src = img.currentSrc || img.src;
        }
      }
    }
  }

  msg(text, ok) {
    if (!this.msgEl) return;
    this.msgEl.textContent = text || '';
    this.msgEl.hidden = !text;
    this.msgEl.classList.toggle('is-ok', !!ok);
  }

  // a required option (Size) is still empty: say so, shake it, bring it into view
  need(scroll) {
    const g = this.groups.find((x) => this.sel[Number(x.dataset.opt)] == null);
    if (!g) return;
    const what = g.classList.contains('is-size') ? 'a size' : 'a ' + (g.dataset.label || 'option').toLowerCase();
    this.msg('Please choose ' + what);
    if (scroll) {
      this.scrollToEl(g);
      const r = g.querySelector('input[type="radio"]');
      if (r) r.focus({ preventScroll: true });
    }
    if (!still()) {
      g.classList.remove('is-shake');
      void g.offsetWidth;
      g.classList.add('is-shake');
    }
  }

  scrollToEl(el) {
    const lenis = window.ieLenis;
    const r = el.getBoundingClientRect();
    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(el, { offset: -(window.innerHeight / 2 - r.height / 2), immediate: still() });
    } else {
      el.scrollIntoView({ block: 'center', behavior: still() ? 'auto' : 'smooth' });
    }
  }

  // now = "or Buy It Now": add, then go straight to checkout
  async add(now) {
    const v = this.variant;
    if (!v) { this.need(false); return; }
    if (!v.available || this.busy) return;
    this.busy = true;
    const qty = this.qty();
    const btns = [this.atc, this.buyNow, ...this.stBtns];
    btns.forEach((b) => { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
    if (!now) {
      this.atcText.textContent = 'Adding...';
      this.stBtns.forEach((b) => { b.textContent = 'Adding...'; });
    }
    this.msg('');
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const body = new FormData();
    body.append('id', v.id);
    body.append('quantity', String(qty));
    // keep any line item properties / selling plan the form may carry
    new FormData(this.form).forEach((val, key) => { if (/^(properties\[|selling_plan)/.test(key)) body.append(key, val); });
    if (sectionIds.length) body.append('sections', sectionIds.join(','));
    let ok = false;
    try {
      const res = await fetch(root + 'cart/add.js', { method: 'POST', body, headers: { Accept: 'application/json' } });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart. Please try again.');
      ok = true;
      if (now) {
        window.location.href = root + 'checkout';
        return;
      }
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(v.id), quantity: qty }],
        promise: deferred.promise,
      }));
      try {
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'ie-product', itemCount: qty, productId: String(this.d.id), sections: json.sections, didError: false },
        });
      } catch (e) { /* the line is in the cart; the drawer still opens below */ }
      const drawer = document.getElementById('cart-drawer');
      setTimeout(() => {
        if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
        else if (!drawer) window.location.href = root + 'cart';
      }, 150);
    } catch (e) {
      if (!ok) this.msg(e.message || 'Something went wrong. Please try again.');
    } finally {
      this.busy = false;
      btns.forEach((b) => { b.removeAttribute('aria-busy'); b.disabled = false; });
      this.update(false);
    }
  }

  // ---------- gallery ----------
  bindGallery(sig) {
    this.track = this.q('[data-track]');
    if (!this.track) return;
    this.main = this.q('[data-main]');
    this.slides = [...this.track.querySelectorAll('[data-slide]')];
    this.thumbBox = this.q('[data-thumbs]');
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.count = this.q('[data-count]');
    this.idx = 0;
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb)), sig));
    this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => this.go(this.idx + Number(b.dataset.step)), sig));
    let raf = 0;
    this.track.addEventListener('scroll', () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const w = this.track.clientWidth || 1;
        const i = Math.round(this.track.scrollLeft / w);
        if (i !== this.idx && i >= 0 && i < this.slides.length) this.setIdx(i);
      });
    }, { passive: true, signal: sig.signal });
    // hover zoom follows the mouse
    this.main.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const r = this.main.getBoundingClientRect();
      this.main.style.setProperty('--zx', (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%');
      this.main.style.setProperty('--zy', (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%');
    }, sig);
    // ask for a sharper file once the mouse is over the photo (zoom is 1.9x)
    this.main.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      this.zoomed = true;
      this.sharpen(this.idx);
    }, sig);
  }

  sharpen(i) {
    if (!this.zoomed || !this.slides[i]) return;
    const img = this.slides[i].querySelector('img');
    const w = Math.ceil(this.main.clientWidth * 1.9);
    if (img && img.getAttribute('sizes') !== w + 'px') {
      img.loading = 'eager';
      img.sizes = w + 'px';
    }
  }

  go(i) {
    if (!this.slides || !this.slides.length) return;
    const n = this.slides.length;
    i = ((i % n) + n) % n;
    this.slides[i].querySelector('img')?.setAttribute('loading', 'eager');
    this.track.scrollTo({ left: i * this.track.clientWidth, behavior: still() ? 'auto' : 'smooth' });
    this.setIdx(i);
  }

  setIdx(i) {
    this.idx = i;
    this.slides.forEach((s, j) => s.classList.toggle('is-on', j === i));
    this.thumbs.forEach((t, j) => {
      t.classList.toggle('is-on', j === i);
      if (j === i) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
    if (this.count) this.count.textContent = (i + 1) + ' / ' + this.slides.length;
    this.sharpen(i);
    // keep the active thumbnail visible inside its own scroller (never scroll the page)
    const box = this.thumbBox;
    const t = this.thumbs[i];
    if (box && t) {
      const br = box.getBoundingClientRect();
      const tr = t.getBoundingClientRect();
      if (getComputedStyle(box).flexDirection === 'column') {
        if (tr.top < br.top) box.scrollTop += tr.top - br.top - 4;
        else if (tr.bottom > br.bottom) box.scrollTop += tr.bottom - br.bottom + 4;
      } else if (tr.left < br.left) box.scrollLeft += tr.left - br.left - 4;
      else if (tr.right > br.right) box.scrollLeft += tr.right - br.right + 4;
    }
  }

  fitTrack() {
    if (this.track) this.track.scrollTo({ left: this.idx * this.track.clientWidth, behavior: 'auto' });
  }

  // ---------- Find My Size ----------
  bindFms(sig) {
    const btn = this.q('[data-fms-btn]');
    const box = this.q('[data-fms]');
    if (!btn || !box || !this.d.chart) return;
    this.fmsIn = box.querySelector('[data-fms-in]');
    this.fmsRes = box.querySelector('[data-fms-res]');
    btn.addEventListener('click', () => {
      const open = box.hidden;
      box.hidden = !open;
      btn.setAttribute('aria-expanded', String(open));
      if (open) this.fmsIn.focus();
    }, sig);
    box.querySelector('[data-fms-go]').addEventListener('click', () => this.findSize(), sig);
    // Enter in the box must not submit the product form
    this.fmsIn.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.findSize(); }
    }, sig);
  }

  findSize() {
    const ch = this.d.chart;
    const val = parseFloat(this.fmsIn.value);
    if (!(val > 0)) { this.fmsRes.textContent = 'Enter your measurement as a number.'; return; }
    const col = ch.cm ? 1 : 2;
    const unit = ch.cm ? ' cm' : '"';
    const g = this.groups.find((x) => Number(x.dataset.opt) === this.d.sizeIndex);
    if (!g) return;
    const radios = [...g.querySelectorAll('input[type="radio"]')];
    const have = new Set(radios.map((r) => r.value));
    const rows = ch.rows.map((r) => [String(r[0]).trim(), parseFloat(r[col])]).filter((r) => have.has(r[0]) && r[1] > 0).sort((a, b) => a[1] - b[1]);
    if (!rows.length) { this.fmsRes.textContent = 'Message us on WhatsApp and we will help you pick a size.'; return; }
    const hit = rows.find((r) => r[1] >= val);
    if (!hit) {
      const last = rows[rows.length - 1];
      this.fmsRes.innerHTML = 'That\'s above our largest size (<b>' + esc(last[0]) + '</b>, ' + last[1] + esc(unit) + '). '
        + '<a href="https://wa.me/' + esc(this.d.wa) + '?text=' + esc(this.d.waSize) + '" target="_blank" rel="noopener">Message us on WhatsApp</a> and we\'ll help.';
      return;
    }
    const r = radios.find((x) => x.value === hit[0]);
    r.checked = true;
    this.sel[this.d.sizeIndex] = hit[0];
    this.update(true);
    this.fmsRes.innerHTML = 'We suggest <b>' + esc(hit[0]) + '</b> (fits up to ' + hit[1] + esc(unit) + '). Selected for you.';
  }

  // ---------- Copy Link ----------
  bindShare(sig) {
    const b = this.q('[data-share]');
    if (!b) return;
    b.addEventListener('click', () => {
      let url = b.dataset.url;
      if (this.variant && this.groups.length) url += '?variant=' + this.variant.id;
      try {
        const c = navigator.clipboard && navigator.clipboard.writeText(url);
        if (c && c.catch) c.catch(() => {});
      } catch (e) { /* clipboard blocked: still confirm */ }
      const t = b.querySelector('[data-share-text]');
      const ic = b.querySelector('[data-share-ic]');
      const okIc = b.querySelector('.ie-pd__share-ok');
      b.classList.add('is-done');
      t.textContent = 'Link Copied';
      ic.hidden = true;
      okIc.hidden = false;
      clearTimeout(this.shareT);
      this.shareT = setTimeout(() => {
        b.classList.remove('is-done');
        t.textContent = 'Copy Link';
        ic.hidden = false;
        okIc.hidden = true;
      }, 2200);
    }, sig);
  }

  // ---------- scroll: sticky bars + reveal ----------
  setBar(el, on) {
    if (!el || el.classList.contains('is-on') === on) return;
    el.classList.toggle('is-on', on);
    el.inert = !on;
  }

  tick() {
    if (this.why && this.why.classList.contains('is-anim') && !this.why.classList.contains('is-in')) {
      if (this.why.getBoundingClientRect().top < window.innerHeight * 0.85) this.why.classList.add('is-in');
    }
    if (!this.top && !this.bot) return;
    const r = this.atc.getBoundingClientRect();
    if (!r.height) return;
    const hh = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const past = r.bottom < hh;
    const desk = matchMedia(DESK).matches;
    if (!this.footer || !this.footer.isConnected) this.footer = document.querySelector('.shopify-section-group-footer-group, footer');
    const foot = !!this.footer && this.footer.getBoundingClientRect().top < window.innerHeight;
    const topOn = !!this.top && past && desk;
    const botOn = !!this.bot && past && !desk && !foot;
    if (topOn) {
      const pw = document.querySelector('.page-wrapper');
      const sbw = pw && /auto|scroll/.test(getComputedStyle(pw).overflowY) ? pw.offsetWidth - pw.clientWidth : 0;
      this.top.style.setProperty('--ie-pd-sbw', sbw + 'px');
    }
    this.setBar(this.top, topOn);
    this.setBar(this.bot, botOn);
    this.classList.toggle('has-top', topOn);
    document.body.classList.toggle('ie-has-sticky-bar', botOn);
    if (botOn) document.body.style.setProperty('--ie-sticky-bar-h', this.bot.offsetHeight + 'px');
  }
}

if (!customElements.get('ie-product')) customElements.define('ie-product', IeProduct);
